import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  UserCheck, Package, GraduationCap, Stethoscope, HeartPulse, Timer,
  Plus, X, Loader2, AlertTriangle, CheckCircle2, Circle, ChevronRight, FileText
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import AdminPlanning from "@/pages/planning/AdminPlanning";
import { AccidentsTab, ParcoursTab, DossiersTab, DocumentsLinkedTab, CollaborateursTab, FicheCollaborateur, AdminAnalyse } from "./AdminPersonnelLot3";

const TABS = [
  { k: "overview", l: "Vue d'ensemble" },
  { k: "integrations", l: "Intégrations" },
  { k: "absences", l: "Absences & arrêts" },
  { k: "equipements", l: "Équipements" },
  { k: "formations", l: "Formations" },
  { k: "accidents", l: "Accidents du travail" },
  { k: "parcours", l: "Parcours & évolutions" },
  { k: "dossiers", l: "Dossiers RH" },
  { k: "documents", l: "Documents" },
  { k: "collaborateurs", l: "Collaborateurs" },
  { k: "analyse", l: "Analyse" },
  { k: "planning", l: "Administration Planning" },
];
const ABS_COLORS = ["#ef4444", "#10b981", "#8b5cf6", "#94a3b8"];
const INT_STATUS = { a_demarrer: "À démarrer", en_cours: "En préparation", terminee: "Intégré", abandonnee: "Abandonnée" };
const INT_STATUS_CLS = { a_demarrer: "bg-blue-100 text-blue-700", en_cours: "bg-amber-100 text-amber-700", terminee: "bg-emerald-100 text-emerald-700", abandonnee: "bg-slate-200 text-slate-500" };
const ARR_STATUS_CLS = { declare: "bg-rose-100 text-rose-700", en_attente_doc: "bg-amber-100 text-amber-700", doc_recu: "bg-blue-100 text-blue-700", verifie: "bg-purple-100 text-purple-700", traite: "bg-emerald-100 text-emerald-700", prolonge: "bg-orange-100 text-orange-700", clos: "bg-slate-200 text-slate-500" };

export default function AdminPersonnel() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("sub") || "overview");
  const [fiche, setFiche] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/rh/admin-personnel/dashboard"); setDash(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="rh-admin-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Administration du personnel</h1>
          <p className="text-slate-500 mt-1 text-sm">Suivi des intégrations, absences, formations et gestion administrative du personnel.</p>
        </div>
        <button data-testid="adp-new" className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
          <Plus className="w-4 h-4"/> Nouvelle action
        </button>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Kpi icon={UserCheck} c="blue" title="Intégrations en cours" v={dash.kpis.integrations_encours.value} testid="adp-kpi-integ"/>
          <Kpi icon={Package} c="amber" title="Équipements à prévoir" v={dash.kpis.equipements_prevus.value} testid="adp-kpi-eq"/>
          <Kpi icon={GraduationCap} c="purple" title="Formations en cours" v={dash.kpis.formations_encours.value} s={`dont ${dash.kpis.formations_encours.retard} en retard`} testid="adp-kpi-form"/>
          <Kpi icon={Stethoscope} c="emerald" title="Absences en cours" v={dash.kpis.absences_encours.value} testid="adp-kpi-abs"/>
          <Kpi icon={HeartPulse} c="rose" title="Arrêts en cours" v={dash.kpis.arrets_encours.value} testid="adp-kpi-arr"/>
          <Kpi icon={Timer} c="orange" title="Délai moyen" v={dash.kpis.delai_moyen.value} unit="j" testid="adp-kpi-delai"/>
        </div>
      )}

      <div className="flex gap-1.5 border-b border-slate-200 pb-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`adp-tab-${t.k}`}
                  className={`px-4 py-2 rounded-t-xl text-sm font-semibold whitespace-nowrap ${tab === t.k ? "bg-white border border-slate-200 border-b-white -mb-px text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{t.l}</button>
        ))}
      </div>

      {loading && !dash ? <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : dash && (
        <>
          {tab === "overview" && <Overview dash={dash}/>}
          {tab === "integrations" && <IntegrationsTab reload={load}/>}
          {tab === "absences" && <AbsencesTab dash={dash} reload={load}/>}
          {tab === "equipements" && <EquipementsTab rows={dash.equipements_prevus}/>}
          {tab === "formations" && <FormationsTab/>}
          {tab === "documents" && <><DocumentsLinkedTab/><DocumentsTab pending={dash.doc_pending}/></>}
          {tab === "accidents" && <AccidentsTab onFiche={setFiche}/>}
          {tab === "parcours" && <ParcoursTab onFiche={setFiche}/>}
          {tab === "dossiers" && <DossiersTab onFiche={setFiche}/>}
          {tab === "collaborateurs" && <CollaborateursTab onFiche={setFiche}/>}
          {tab === "analyse" && <AdminAnalyse/>}
          {tab === "planning" && <AdminPlanning/>}
          {fiche && <FicheCollaborateur id={fiche} onClose={() => setFiche(null)}/>}
        </>
      )}
    </div>
  );
}

function Overview({ dash }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Intégrations récentes */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="adp-integ-recentes">
          <div className="px-4 py-3 border-b border-slate-100"><h3 className="font-display text-base font-bold text-slate-900">Intégrations récentes</h3></div>
          <div className="divide-y divide-slate-100 max-h-[340px] overflow-y-auto">
            {dash.integrations_recentes.map(i => (
              <div key={i.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`adp-int-${i.id}`}>
                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-xs shrink-0">{(i.collab_nom || "?").slice(0, 2).toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">{i.collab_nom}</div>
                  <div className="text-[11px] text-slate-500 truncate">{i.poste || "—"} · {i.corner || "—"} · {i.date_arrivee}</div>
                </div>
                <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden shrink-0">
                  <div className={`h-full ${i.progress >= 80 ? "bg-emerald-500" : i.progress >= 40 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${i.progress}%` }}/>
                </div>
                <span className="text-[10px] font-bold text-slate-500 w-8 text-right">{i.progress}%</span>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${INT_STATUS_CLS[i.statut] || "bg-slate-100"}`}>{INT_STATUS[i.statut] || i.statut}</span>
              </div>
            ))}
            {dash.integrations_recentes.length === 0 && <div className="py-12 text-center text-slate-400 text-sm italic">Aucune intégration en cours</div>}
          </div>
        </div>
        {/* Absences donut */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="adp-abs-donut">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Absences en cours</h3>
          <div style={{ height: 220 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={dash.absences_donut} dataKey="count" nameKey="l" innerRadius={44} outerRadius={70} paddingAngle={2}>
                  {dash.absences_donut.map((_, i) => <Cell key={i} fill={ABS_COLORS[i % ABS_COLORS.length]}/>)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 11 }}/>
                <Legend wrapperStyle={{ fontSize: 11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Arrêts */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="adp-arrets">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Arrêts de travail en cours</h3>
          <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
            {dash.arrets_encours.map(a => (
              <div key={a.id} className="py-2 flex items-center gap-2">
                <HeartPulse className="w-4 h-4 text-rose-500 shrink-0"/>
                <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-slate-800 truncate">{a.collab_nom}</div><div className="text-[10px] text-slate-500">{a.corner || "—"} · {a.date_debut}</div></div>
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${ARR_STATUS_CLS[a.statut] || "bg-slate-100"}`}>{a.statut}</span>
              </div>
            ))}
            {dash.arrets_encours.length === 0 && <div className="py-8 text-center text-slate-400 text-xs italic">Aucun arrêt</div>}
          </div>
        </div>
        {/* Équipements */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="adp-eq-overview">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Équipements à prévoir</h3>
          <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
            {dash.equipements_prevus.map((e, i) => (
              <div key={i} className="py-2 flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-500 shrink-0"/>
                <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-slate-800 truncate">{e.collab_nom}</div><div className="text-[10px] text-slate-500">{e.corner || "—"} · {e.date_arrivee}</div></div>
                <span className={`text-[11px] font-bold ${e.urgence === "critique" ? "text-rose-600" : e.urgence === "attention" ? "text-orange-600" : "text-slate-500"}`}>{e.jours_restants}j</span>
              </div>
            ))}
            {dash.equipements_prevus.length === 0 && <div className="py-8 text-center text-slate-400 text-xs italic">RAS</div>}
          </div>
        </div>
        {/* Formations */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="adp-form-overview">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Formations en cours</h3>
          <div className="divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
            {dash.formations_encours.map(f => (
              <div key={f.id} className="py-2 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-purple-500 shrink-0"/>
                <div className="flex-1 min-w-0"><div className="text-xs font-semibold text-slate-800 truncate">{f.intitule}</div><div className="text-[10px] text-slate-500">{f.collab_nom} · {f.corner || "—"}</div></div>
                <span className="text-[10px] font-semibold text-slate-500">{f.date_prevue || "—"}</span>
              </div>
            ))}
            {dash.formations_encours.length === 0 && <div className="py-8 text-center text-slate-400 text-xs italic">Aucune formation</div>}
          </div>
        </div>
      </div>

      {/* Documents en attente */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <DocCounter icon={FileText} label="Pièces manquantes" v={dash.doc_pending.pieces_manquantes} testid="adp-doc-mq"/>
        <DocCounter icon={FileText} label="À signer" v={dash.doc_pending.a_signer} testid="adp-doc-sign"/>
        <DocCounter icon={FileText} label="Contrats à finaliser" v={dash.doc_pending.contrats_finaliser} testid="adp-doc-ctr"/>
        <DocCounter icon={FileText} label="Arrêts à récupérer" v={dash.doc_pending.arrets_recuperer} testid="adp-doc-arr"/>
      </div>
    </div>
  );
}

function DocCounter({ icon: Icon, label, v, testid }) {
  return (
    <button className="bg-white border border-slate-200 rounded-2xl p-4 hover:border-blue-300 hover:shadow-sm transition text-left" data-testid={testid}>
      <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-slate-400"/><span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span></div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-1">{v}</div>
    </button>
  );
}

function IntegrationsTab({ reload }) {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [detail, setDetail] = useState(null);
  const load = async () => { setLoading(true); try { const r = await api.get("/rh/integrations"); setRows(r.data || []); } catch (e) { toast.error(e.message); } setLoading(false); };
  useEffect(() => { load(); }, []);
  const toggle = async (integ, k, done) => {
    try { await api.patch(`/rh/integrations/${integ.id}/checklist`, { k, done }); await load(); if (detail) setDetail(rows.find(x => x.id === integ.id)); reload(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="adp-tab-integ">
      {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
              <tr><th className="text-left px-3 py-2">Collaborateur</th><th className="text-left px-3 py-2">Poste</th><th className="text-left px-3 py-2">Corner</th><th className="text-left px-3 py-2">Arrivée</th><th className="text-left px-3 py-2">Progression</th><th className="text-left px-3 py-2">Statut</th><th className="text-right px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {rows.map(i => {
                const n = (i.checklist || []).length || 1;
                const done = (i.checklist || []).filter(x => x.done).length;
                const p = Math.round(100 * done / n);
                return (
                  <tr key={i.id} onClick={() => setDetail(i)} className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer" data-testid={`adp-int-row-${i.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{i.collaborateur_id?.slice(0, 12)}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">—</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[180px] truncate">{i.corner || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{i.date_arrivee}</td>
                    <td className="px-3 py-2 min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${p >= 80 ? "bg-emerald-500" : p >= 40 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${p}%` }}/></div>
                        <span className="text-[10px] font-semibold text-slate-500 w-9 text-right">{p}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${INT_STATUS_CLS[i.statut] || "bg-slate-100"}`}>{INT_STATUS[i.statut] || i.statut}</span></td>
                    <td className="px-3 py-2 text-right"><ChevronRight className="w-4 h-4 text-slate-400 inline"/></td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm italic">Aucune intégration</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} className="fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl border-l border-slate-200 flex flex-col" data-testid="adp-int-drawer">
            <div className="px-5 py-4 border-b flex items-center justify-between"><h2 className="font-display text-lg font-bold">Checklist intégration</h2><button onClick={() => setDetail(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button></div>
            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {(detail.checklist || []).map(x => (
                <label key={x.k} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`adp-cl-${x.k}`}>
                  <input type="checkbox" checked={!!x.done} onChange={e => toggle(detail, x.k, e.target.checked)}/>
                  <span className={`text-sm font-semibold ${x.done ? "line-through text-slate-400" : "text-slate-800"}`}>{x.l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AbsencesTab({ dash }) {
  const [tab, setTab] = useState("absences");
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  const load = async () => { setLoading(true); try { const path = tab === "absences" ? "/rh/absences" : "/rh/arrets"; const r = await api.get(path); setRows(r.data || []); } catch (e) { toast.error(e.message); } setLoading(false); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  return (
    <div className="space-y-3" data-testid="adp-tab-abs">
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-[11px] text-amber-800">
        <b>Confidentialité :</b> Les données RH sensibles (absences, arrêts, documents personnels) sont visibles uniquement pour les rôles autorisés (Responsable RH, Gestionnaire RH, Admin).
      </div>
      <div className="flex gap-1">
        <button onClick={() => setTab("absences")} className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${tab === "absences" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Absences ({dash.kpis.absences_encours.value})</button>
        <button onClick={() => setTab("arrets")} className={`text-[11px] font-bold px-3 py-1.5 rounded-full ${tab === "arrets" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Arrêts ({dash.kpis.arrets_encours.value})</button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Corner</th><th className="text-left px-3 py-2">Début</th><th className="text-left px-3 py-2">Fin</th><th className="text-left px-3 py-2">Justificatif</th><th className="text-left px-3 py-2">Statut</th></tr>
              </thead>
              <tbody>
                {rows.map(a => (
                  <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adp-abs-row-${a.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{a.type}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate">{a.corner || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{a.date_debut}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{a.date_fin || "—"}</td>
                    <td className="px-3 py-2 text-xs">{a.justificatif_url ? "✔" : (tab === "arrets" ? <span className="text-amber-600 font-bold">Manquant</span> : "—")}</td>
                    <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${ARR_STATUS_CLS[a.statut] || "bg-slate-100 text-slate-600"}`}>{a.statut}</span></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-slate-400 text-sm italic">Aucune ligne</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EquipementsTab({ rows }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="adp-tab-eq">
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
            <tr><th className="text-left px-3 py-2">Collaborateur</th><th className="text-left px-3 py-2">Corner</th><th className="text-left px-3 py-2">Arrivée</th><th className="text-left px-3 py-2">Urgence</th><th className="text-right px-3 py-2">Jours restants</th></tr>
          </thead>
          <tbody>
            {(rows || []).map((e, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`adp-eq-row-${i}`}>
                <td className="px-3 py-2 font-semibold text-slate-800">{e.collab_nom}</td>
                <td className="px-3 py-2 text-slate-600">{e.corner || "—"}</td>
                <td className="px-3 py-2 whitespace-nowrap">{e.date_arrivee}</td>
                <td className="px-3 py-2"><span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${e.urgence === "critique" ? "bg-rose-100 text-rose-700" : e.urgence === "attention" ? "bg-amber-100 text-amber-700" : "bg-slate-100"}`}>{e.urgence}</span></td>
                <td className={`px-3 py-2 text-right font-bold ${e.jours_restants < 3 ? "text-rose-600" : e.jours_restants < 7 ? "text-amber-600" : "text-slate-600"}`}>{e.jours_restants}</td>
              </tr>
            ))}
            {(!rows || rows.length === 0) && <tr><td colSpan={5} className="py-12 text-center text-slate-400 text-sm italic">Aucun équipement à prévoir</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FormationsTab() {
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { setLoading(true); try { const r = await api.get("/rh/formations"); setRows(r.data || []); } catch (e) { toast.error(e.message); } setLoading(false); })(); }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="adp-tab-form">
      {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
              <tr><th className="text-left px-3 py-2">Intitulé</th><th className="text-left px-3 py-2">Date prévue</th><th className="text-left px-3 py-2">Réalisée</th><th className="text-left px-3 py-2">Prestataire</th><th className="text-left px-3 py-2">Obligatoire</th></tr>
            </thead>
            <tbody>
              {rows.map(f => (
                <tr key={f.id} className="border-t border-slate-100" data-testid={`adp-form-row-${f.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-800">{f.intitule}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{f.date_prevue || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{f.date_realisee || <span className="text-slate-400">En cours</span>}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{f.prestataire || "—"}</td>
                  <td className="px-3 py-2">{f.obligatoire ? <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Oui</span> : "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-slate-400 text-sm italic">Aucune formation</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ pending }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="adp-tab-docs">
      <DocCounter icon={FileText} label="Pièces manquantes" v={pending.pieces_manquantes} testid="adp-docs-mq"/>
      <DocCounter icon={FileText} label="Documents à signer" v={pending.a_signer} testid="adp-docs-sign"/>
      <DocCounter icon={FileText} label="Contrats à finaliser" v={pending.contrats_finaliser} testid="adp-docs-ctr"/>
      <DocCounter icon={FileText} label="Arrêts à récupérer" v={pending.arrets_recuperer} testid="adp-docs-arr"/>
    </div>
  );
}

function Kpi({ icon: Icon, c, title, v, unit, s, testid }) {
  const bg = { blue: "bg-blue-50 text-blue-600", emerald: "bg-emerald-50 text-emerald-600", rose: "bg-rose-50 text-rose-600", purple: "bg-purple-50 text-purple-600", amber: "bg-amber-50 text-amber-600", orange: "bg-orange-50 text-orange-600" }[c] || "bg-slate-50 text-slate-600";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${bg} grid place-items-center`}><Icon className="w-4 h-4"/></div>
      <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mt-2">{title}</div>
      <div className="flex items-baseline gap-1"><div className="font-display text-2xl font-bold text-slate-900">{v}</div>{unit && <div className="text-xs font-semibold text-slate-500">{unit}</div>}</div>
      {s && <div className="text-[10px] text-slate-500 mt-0.5">{s}</div>}
    </div>
  );
}
